import { morph } from '@theme/morph';
import { debounce } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Marks a browse-by-topic link as the current one, in class and in ARIA.
 * @param {Element} item - The topic link
 * @param {boolean} active - Whether the topic is the current one
 */
function setTopicActive(item, active) {
  item.classList.toggle('active', active);

  if (active) {
    item.setAttribute('aria-current', 'page');
  } else {
    item.removeAttribute('aria-current');
  }
}

/**
 * A custom element that renders a paginated blog posts list
 */
export default class BlogPostsList extends PaginatedList {
  parser = new DOMParser();

  browseItems = [];
  abortController = null;
  debouncedSearch = null;

  selectors = {
    blogGrid: '.blog-posts-container',
    blogPagination: '.blog-pagination',
    browseByItem: 'a.blog-browse-topic__link',
  };

  connectedCallback() {
    super.connectedCallback();

    this.browseItems = [...this.querySelectorAll(this.selectors.browseByItem)];

    this.initSearch();
    this.initBrowseByTopic();
  }

  disconnectedCallback() {
    this.abortController?.abort();

    const { searchInput } = this.refs;

    if (searchInput && this.debouncedSearch) {
      searchInput.removeEventListener('input', this.debouncedSearch);
    }
  }

  async initSearch() {
    const { searchInput } = this.refs;
    const q = new URL(location).searchParams.get('q');

    this.debouncedSearch = debounce(async () => {
      const searchTerm = searchInput?.value?.trim();

      if (!searchTerm?.length) {
        await this.#resetSearch();
        return;
      }

      await this.#getSearchResults(searchTerm);
    }, 400);

    searchInput?.addEventListener('input', this.debouncedSearch);

    if (q) {
      if (searchInput) searchInput.value = q;

      await this.#getSearchResults(q);
    }
  }

  initBrowseByTopic() {
    if (!this.browseItems.length) return;

    this.browseItems.forEach(item => {
      const url = item.getAttribute('href');
      const locationUrl = location.pathname.at(-1) === '/' ? location.pathname.slice(0, -1) : location.pathname;

      if (url === locationUrl) setTopicActive(item, true);

      item.addEventListener('click', async (e) => {
        e.preventDefault();

        const isActive = item.classList.contains('active');

        if (!url || isActive) return;

        await this.#getBrowseBy(url, item);
      });
    });
  }

  /**
   *
   * @param {String} url
   * @param {Element} item
   */
  async #getBrowseBy(url, item) {
    const { grid, pagination } = this.refs;

    grid?.classList.add('loading');

    try {
      this.abortController?.abort();
      this.abortController = new AbortController();

      await this.#resetSearch(false);

      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${ response.status }`);
      }

      const html = await response.text();

      if (!html.includes('blog-posts-container')) {
        throw new Error('Invalid markup received');
      }

      const doc = this.parser?.parseFromString(html, 'text/html');
      const updatedGrid = doc?.querySelector(this.selectors.blogGrid);
      const updatedPagination = doc?.querySelector(this.selectors.blogPagination);

      if (grid && updatedGrid) {
        this.browseItems.forEach(other => setTopicActive(other, false));

        setTopicActive(item, true);
        window.history.pushState({}, '', url);

        morph(grid, updatedGrid);
      }

      if (pagination && pagination instanceof Node && updatedPagination) {
        morph(pagination, updatedPagination);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    } finally {
      grid?.classList.remove('loading');
    }
  }

  /**
   *
   * @param {String} searchTerm
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const { grid, terms, termsText } = this.refs;

    const defaultUrl = this.dataset.blogUrl;
    const apiUrl = new URL(Theme.routes.predictive_search_url, location.origin);

    try {
      grid?.classList.add('loading');

      this.abortController?.abort();
      this.abortController = new AbortController();

      window.history.pushState({}, '', defaultUrl);

      this.browseItems.forEach(item => {
        setTopicActive(item, Boolean(item.dataset.default));
      });

      apiUrl.searchParams.set('q', searchTerm);
      apiUrl.searchParams.set('resources[type]', 'article');

      const resultsMarkup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, apiUrl);

      if (!resultsMarkup || !grid) return;
      if (typeof resultsMarkup === 'string' && !resultsMarkup.includes('blog-post')) {
        throw new Error('Invalid search markup');
      }

      const url = new URL(location);

      url.searchParams.set('q', searchTerm);
      window.history.replaceState({}, '', url);

      morph(grid, resultsMarkup);

      if (termsText && termsText instanceof Node) {
        termsText.textContent = searchTerm;
      }

      if (terms && terms instanceof Node) {
        terms.classList.remove('hidden');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    } finally {
      grid?.classList.remove('loading');
    }
  }

  /**
   *
   * @param {Boolean} shouldUpdateGrid
   */
  async #resetSearch(shouldUpdateGrid = true) {
    const { grid, pagination, searchInput, terms, termsText } = this.refs;

    const url = new URL(location);
    const defaultUrl = this.dataset.blogUrl;

    if (!defaultUrl || !grid) return;

    try {
      if (searchInput) searchInput.value = '';

      url.searchParams.delete('q');
      window.history.replaceState({}, '', url);

      if (terms && terms instanceof Node) terms.classList.add('hidden');
      if (termsText && termsText instanceof Node) termsText.textContent = '';

      if (!shouldUpdateGrid) return;

      grid.classList.add('loading');

      this.abortController?.abort();
      this.abortController = new AbortController();

      const response = await fetch(defaultUrl, {
        signal: this.abortController.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const markup = await response.text();

      if (!markup.includes('blog-posts-container')) {
        throw new Error('Invalid markup received');
      }

      const doc = this.parser.parseFromString(markup, 'text/html');
      const updatedGrid = doc.querySelector(this.selectors.blogGrid);
      const updatedPagination = doc.querySelector(this.selectors.blogPagination);

      if (updatedGrid) {
        morph(grid, updatedGrid);
      }

      if (pagination && pagination instanceof Node && updatedPagination) {
        morph(pagination, updatedPagination);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    } finally {
      grid.classList.remove('loading');
    }
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
